import { useEffect, useState } from "react";
import GradientText from "../components/GradientText";
import Seo from "../components/Seo";

const neonColors = ["#fc03f4", "#03f0fc", "#fc03f4", "#03f0fc", "#fc03f4"];

interface MenuItem {
  name: string;
  description: string;
  price?: number;
  image?: string;
  tags?: string[];
}

interface MenuModifier {
  name: string;
  price?: number;
}

interface MenuModifierGroup {
  title: string;
  description?: string;
  items: MenuModifier[];
}

interface MenuModifiersContent {
  title: string;
  description?: string;
}

interface MenuCategory {
  name: string;
  id: string;
  items: MenuItem[];
  modifiers?: MenuModifierGroup[];
  modifiersContent?: MenuModifiersContent;
}

interface MenuData {
  categories: MenuCategory[];
}

const tagLabels: Record<string, string> = {
  popular: "Popular",
  signature: "Signature",
  vegetarian: "Vegetarian",
  spicy: "Spicy",
  new: "New",
};

function formatPrice(price?: number): string {
  return typeof price === "number" ? `$${price.toFixed(2)}` : "Market Price";
}

export default function MenuPage() {
  const [menuData, setMenuData] = useState<MenuData | null>(null);
  const [currentFilter, setCurrentFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    async function loadMenu() {
      try {
        const response = await fetch("/menu.json");
        if (!response.ok) throw new Error("Failed to load menu");
        const data = await response.json();
        setMenuData(data);
        setLoading(false);
      } catch (err) {
        console.error("Error loading menu:", err);
        setError(true);
        setLoading(false);
      }
    }
    loadMenu();
  }, []);

  const handleFilterClick = (category: string) => {
    setCurrentFilter(category);
  };

  const filteredCategories =
    menuData?.categories.filter(
      (cat) => currentFilter === "all" || cat.id === currentFilter,
    ) || [];

  return (
    <>
      <Seo
        title="Menu"
        description="Browse the Bull on the Loose menu featuring pit beef sandwiches, smoky BBQ, delicous sides, and more!"
        path="/menu"
        image="/images/PitBeef.webp"
      />
      {/* Page Header */}
      <section className="relative pt-[calc(80px+60px)] pb-[60px] px-6 text-center overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 page-header-gradient-bg"></div>
          <div className="absolute inset-0 hero-grid-bg"></div>
        </div>
        <div className="container">
          <GradientText
            colors={neonColors}
            animationSpeed={6}
            className="text-[clamp(2.5rem,8vw,4.5rem)] mb-4 font-display font-semibold"
          >
            Our Menu
          </GradientText>
          <p className="text-xl text-text-secondary font-light">
            Bold flavors, made fresh to order
          </p>
        </div>
      </section>

      {/* Menu Section */}
      <section className="py-[60px] pb-[100px] bg-bg-secondary min-h-[60vh]">
        <div className="container">
          {/* Filters */}
          <div
            className="flex justify-center gap-3 mb-[60px] flex-wrap"
            id="menu-filters"
          >
            {["all", "sandwiches", "platters", "sides", "drinks"].map((cat) => (
              <button
                key={cat}
                className={`filter-btn ${
                  currentFilter === cat ? "active" : ""
                }`}
                onClick={() => handleFilterClick(cat)}
              >
                {cat === "all"
                  ? "All Items"
                  : cat.charAt(0).toUpperCase() + cat.slice(1)}
              </button>
            ))}
          </div>

          {/* Menu Grid */}
          <div
            className="flex flex-col gap-5 max-w-[900px] mx-auto"
            id="menu-grid"
            key={currentFilter}
          >
            {loading && (
              <div className="text-center py-20 text-text-secondary">
                <div className="loading-spinner"></div>
                <p>Loading delicious items...</p>
              </div>
            )}

            {error && (
              <div className="text-center py-20">
                <span className="text-[4rem] block mb-5">😅</span>
                <h3 className="font-body font-semibold text-text-primary mb-2">
                  Oops! Couldn't load the menu
                </h3>
                <p className="text-text-secondary">
                  Please try refreshing the page
                </p>
              </div>
            )}

            {!loading &&
              !error &&
              filteredCategories.map((category) => (
                <div key={`${currentFilter}-${category.id}-${category.name}`}>
                  {/* Category Header */}
                  <div className="py-[30px] pb-[15px]">
                    <h2 className="text-[2rem] text-text-primary relative inline-block font-display font-semibold">
                      {category.name}
                      <span className="absolute -bottom-2 left-0 w-[60px] h-[3px] rounded bg-gradient-to-r from-accent-blue via-accent-pink to-accent-purple"></span>
                    </h2>
                  </div>

                  {/* Menu Items */}
                  {category.items.map((item) => (
                    <MenuItemCard
                      key={`${currentFilter}-${category.id}-${category.name}-${item.name}`}
                      item={item}
                    />
                  ))}

                  {category.modifiers && category.modifiers.length > 0 && (
                    <CategoryModifiers
                      modifiers={category.modifiers}
                      content={category.modifiersContent}
                    />
                  )}
                </div>
              ))}
          </div>
        </div>
      </section>
    </>
  );
}

function MenuItemCard({ item }: { item: MenuItem }) {
  const hasImage = item.image && item.image.trim() !== "";
  const tags = item.tags || [];

  return (
    <div className="flex flex-col md:flex-row bg-bg-card border border-border-default rounded-lg overflow-hidden transition-all duration-300 hover:border-accent-purple hover:shadow-md-custom hover:shadow-glow-pink animate-slide-up">
      {hasImage && (
        <div className="w-full md:w-[180px] md:min-w-[180px] h-[160px] md:h-[140px] overflow-hidden relative bg-bg-elevated">
          <img
            src={item.image}
            alt={item.name}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-400 hover:scale-105"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.parentElement!.innerHTML =
                '<div class="w-full h-full flex items-center justify-center bg-gradient-to-br from-bg-elevated to-bg-tertiary"><span class="text-[2.5rem] opacity-50">🍽️</span></div>';
            }}
          />
        </div>
      )}
      <div className="p-4 md:px-5 flex-1 flex flex-col justify-center">
        <div className="flex justify-between items-start gap-4 mb-2">
          <h3 className="font-body font-semibold text-lg text-text-primary">
            {item.name}
          </h3>
          <span className="font-bold text-base text-accent-pink whitespace-nowrap">
            {formatPrice(item.price)}
          </span>
        </div>
        <p className="text-text-secondary text-[0.9rem] leading-relaxed mb-[10px]">
          {item.description}
        </p>
        {tags.length > 0 && (
          <div className="flex gap-[6px] flex-wrap">
            {tags.map((tag) => (
              <span key={tag} className={`tag tag-${tag}`}>
                {tagLabels[tag] || tag}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function CategoryModifiers({
  modifiers,
  content,
}: {
  modifiers: MenuModifierGroup[];
  content?: MenuModifiersContent;
}) {
  return (
    <details className="mt-4 overflow-hidden rounded-lg border border-border-default bg-bg-card transition-all duration-300 hover:border-accent-purple">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-5 py-4 text-left">
        <div>
          <p className="font-body text-base font-semibold text-text-primary">
            {content?.title || "Options & Modifiers"}
          </p>
          {content?.description && (
            <p className="mt-1 text-sm text-text-secondary">
              {content.description}
            </p>
          )}
        </div>
        <span className="text-sm font-medium text-accent-pink">Show</span>
      </summary>

      <div className="border-t border-border-default px-5 py-5">
        <div className="flex flex-col gap-5">
          {modifiers.map((group) => (
            <div key={group.title} className="rounded-xl bg-bg-elevated/70 p-4">
              <div className="mb-3">
                <h3 className="font-body text-sm font-semibold uppercase tracking-[0.16em] text-accent-blue">
                  {group.title}
                </h3>
                {group.description && (
                  <p className="mt-1 text-sm text-text-secondary">
                    {group.description}
                  </p>
                )}
              </div>

              <ul className="grid gap-2 sm:grid-cols-2">
                {group.items.map((modifier) => (
                  <li
                    key={`${group.title}-${modifier.name}`}
                    className="flex items-center justify-between gap-4 rounded-lg border border-border-default/70 bg-bg-card px-3 py-2"
                  >
                    <span className="text-sm text-text-primary">
                      {modifier.name}
                    </span>
                    <span className="whitespace-nowrap text-sm font-semibold text-accent-pink">
                      {modifier.price === 0
                        ? "Free"
                        : formatPrice(modifier.price)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </details>
  );
}
